import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { messagesApi, type ConversationMessage } from '../api/endpoints.ts';
import { Alert, Button, EmptyState, TextInput } from './ui.tsx';
import { MessageRow } from './conversation-timeline.tsx';
import {
  CALL_UNDER_WAY_POLL_MS,
  callDurationSeconds,
  callOutcomeLabel,
  formatCallDuration,
  hasCallUnderWay,
  isAnswerableInbound,
  replyChannelFor,
  unplacedCallLabel,
} from './call-details.tsx';
import { MessageComposer, type ReplyContext } from './message-composer.tsx';
import { useSession } from '../state/session.tsx';
import { getDateLocale } from '../i18n/locale.ts';

const PAGE_SIZE = 20;
const RECENT_COUNT = 3;
const CHANNELS = ['all', 'email', 'sms', 'internal', 'call'] as const;
type Channel = (typeof CHANNELS)[number];

export function isCustomerHistory(search: string) {
  return new URLSearchParams(search).get('history') === '1';
}

/** Customer-specific reading surface. The shared composer and message row own
 * sending, reply addressing, attachments, delivery details, and visit actions.
 * The overview is a summary, while server pages keep ALL older messages reachable.
 */
// Shared with the customer page so the lookup starts alongside the customer
// request instead of waiting for this card to mount.
export function customerConversationQuery(customerId: string) {
  return {
    queryKey: ['conversations', 'customer', customerId] as const,
    queryFn: () => messagesApi.conversations({ customerId, pageSize: 1 }),
  };
}

function CommunicationSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-live="polite" className="mb-4 flex flex-col gap-2">
      <span className="sr-only">{label}</span>
      {[0, 1, 2].map((row) => (
        <div
          key={row}
          aria-hidden="true"
          className="h-12 animate-pulse rounded border border-[color:var(--color-line)] bg-[color:var(--color-line)]/40"
        />
      ))}
    </div>
  );
}

export function CustomerCommunication({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { can, user, company, publicDemo } = useSession();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const history = isCustomerHistory(location.search);
  const requestedPage = Number(params.get('messagePage') ?? 1);
  const page = history && Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const channel = params.get('messageChannel');
  const filter: Channel = CHANNELS.includes(channel as Channel) ? (channel as Channel) : 'all';
  const search = params.get('messageSearch') ?? '';
  const [composing, setComposing] = useState(false);
  const [reply, setReply] = useState<ReplyContext>();
  const replyNonce = useRef(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const historyButton = useRef<HTMLButtonElement>(null);
  const composeButton = useRef<HTMLButtonElement>(null);
  const composer = useRef<HTMLDivElement>(null);
  const previousHistory = useRef(history);
  const focusPage = useRef(false);

  const conversations = useQuery(customerConversationQuery(customerId));
  const conversation = conversations.data?.items[0];
  // The recent page also supplies the standing reply suggestion. Reading an
  // older page must not silently retarget a draft to an older sender/subject.
  const recent = useQuery({
    queryKey: ['conversation-thread', conversation?.id, 'customer-page', 1, PAGE_SIZE],
    queryFn: () => messagesApi.thread(conversation!.id, { page: 1, pageSize: PAGE_SIZE }),
    enabled: Boolean(conversation),
    // A call placed from this page rings, connects and ends on Twilio's
    // callbacks: read again until it has, so the page shows how it went.
    refetchInterval: (query) => (hasCallUnderWay(query.state.data?.messages) ? CALL_UNDER_WAY_POLL_MS : false),
  });
  const older = useQuery({
    queryKey: ['conversation-thread', conversation?.id, 'customer-page', page, PAGE_SIZE],
    queryFn: () => messagesApi.thread(conversation!.id, { page, pageSize: PAGE_SIZE }),
    enabled: Boolean(conversation) && history && page > 1,
  });
  const thread = page === 1 ? recent : older;
  const ordered = [...(thread.data?.messages ?? [])].reverse();
  const lastInbound = [...(recent.data?.messages ?? [])].reverse().find(isAnswerableInbound);
  const suggested: ReplyContext | undefined = lastInbound
    ? {
        channel: replyChannelFor(lastInbound),
        subject: lastInbound.subject,
        replyToAddress: lastInbound.replyToAddress,
        nonce: 0,
      }
    : undefined;

  const marking = useRef(false);
  const markRead = useMutation({
    mutationFn: () => messagesApi.markRead(conversation!.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversations'] }),
    onSettled: () => {
      marking.current = false;
    },
  });

  useEffect(() => {
    if (previousHistory.current !== history) {
      (history ? heading.current : historyButton.current)?.focus();
      previousHistory.current = history;
    } else if (focusPage.current && !thread.isLoading) {
      heading.current?.focus();
      focusPage.current = false;
    }
  }, [history, page, thread.isLoading]);

  useEffect(() => {
    if (composing) composer.current?.querySelector('textarea')?.focus();
  }, [composing]);

  const changeParams = (values: Record<string, string | null>, replace = false) => {
    const next = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(values)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    navigate({ pathname: location.pathname, search: next.toString(), hash: '' }, { replace, preventScrollReset: true });
  };
  const changePage = (nextPage: number) => {
    focusPage.current = true;
    changeParams({ messagePage: String(nextPage) });
  };
  const normalizedSearch = search.trim().toLocaleLowerCase(getDateLocale());
  const matching = ordered.filter((message) => {
    if (filter !== 'all' && message.channel !== filter) return false;
    const text = [
      message.subject,
      message.body,
      message.senderName,
      ...message.attachments.map((file) => file.filename),
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase(getDateLocale());
    return text.includes(normalizedSearch);
  });
  const messages = history ? matching : ordered.slice(0, RECENT_COUNT);
  const loading = conversations.isLoading || (Boolean(conversation) && thread.isLoading);
  const failed = conversations.isError || (Boolean(conversation) && thread.isError);
  const canWrite = user?.role !== 'crew' && !company?.accessLocked && can('customers:write') && can('customers:read');

  return (
    <section className="card mb-6 min-w-0" aria-labelledby="conversation-heading">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 id="conversation-heading" ref={heading} tabIndex={-1} className="text-lg font-semibold">
          {t(history ? 'customers.communication.history' : 'customers.communication.recent')}
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button ref={historyButton} onClick={() => changeParams({ history: history ? null : '1' })}>
            {t(history ? 'customers.communication.overview' : 'customers.communication.viewHistory')}
          </Button>
          {canWrite ? (
            <Button
              ref={composeButton}
              variant="primary"
              aria-expanded={composing}
              aria-controls="customer-composer"
              onClick={() => setComposing((open) => !open)}
            >
              {t(composing ? 'customers.communication.closeComposer' : 'customers.communication.write')}
            </Button>
          ) : null}
          {/* Not in the shared demo: the company is shared, so marking a thread read
              would clear it for every visitor after this one (and the API refuses). */}
          {conversation && conversation.unreadCount > 0 && !publicDemo ? (
            <Button
              busy={markRead.isPending}
              onClick={() => {
                if (marking.current) return;
                marking.current = true;
                markRead.mutate();
              }}
            >
              {t('customers.communication.markRead', { count: conversation.unreadCount })}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Keep one composer mounted across summary/history, filters, and page
          changes. Hiding it must not clear typed work or interrupt a send. */}
      <div
        id="customer-composer"
        ref={composer}
        hidden={!composing}
        className="mb-4 border-b border-[color:var(--color-line)] pb-4"
      >
        <MessageComposer customerId={customerId} replyContext={reply ?? suggested} />
        <Button
          onClick={() => {
            setComposing(false);
            composeButton.current?.focus();
          }}
        >
          {t('customers.communication.closeComposer')}
        </Button>
      </div>

      {markRead.isError ? <Alert tone="error">{t('customers.communication.readError')}</Alert> : null}
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        {t(history ? 'customers.communication.historyHint' : 'customers.communication.recentHint')}
      </p>
      {history ? (
        <div className="mb-4">
          <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label={t('customers.communication.filterLabel')}>
            {CHANNELS.map((value) => (
              <Button
                key={value}
                aria-pressed={filter === value}
                variant={filter === value ? 'primary' : 'secondary'}
                onClick={() => changeParams({ messageChannel: value }, true)}
              >
                {t(`customers.communication.channel.${value}`)}
              </Button>
            ))}
          </div>
          <TextInput
            label={t('customers.communication.search')}
            value={search}
            hint={t('customers.communication.searchHint')}
            onChange={(event) => changeParams({ messageSearch: event.target.value || null }, true)}
          />
        </div>
      ) : null}

      {failed ? (
        <div className="mb-4">
          <Alert tone="error">{t('customers.communication.loadError')}</Alert>
          <Button
            busy={conversations.isFetching || thread.isFetching}
            onClick={() => {
              if (conversations.isError) void conversations.refetch();
              if (thread.isError) void thread.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}
      {loading && !failed ? <CommunicationSkeleton label={t('customers.communication.loading')} /> : null}
      {!loading && !failed && messages.length === 0 ? (
        <EmptyState
          title={t(
            history && (filter !== 'all' || normalizedSearch)
              ? 'customers.communication.noMatch'
              : 'customers.communication.empty',
          )}
        >
          <p>{t(history ? 'customers.communication.emptyPageHint' : 'customers.communication.emptyHint')}</p>
        </EmptyState>
      ) : null}
      <ol className="flex min-w-0 flex-col gap-2" aria-label={t('customers.communication.listLabel')}>
        {messages.map((message) => (
          <li key={`${page}:${message.id}`}>
            <details className="rounded border border-[color:var(--color-line)]">
              <summary className="cursor-pointer rounded p-3 text-sm focus-visible:outline-2 focus-visible:outline-offset-2">
                <span className="ml-1 font-medium">{t(`customers.communication.channel.${message.channel}`)}</span>
                <span className="ml-2 text-xs text-[color:var(--color-muted)]">
                  {t(`customers.communication.direction.${message.direction}`)} ·{' '}
                  <time dateTime={message.createdAt}>
                    {new Date(message.createdAt).toLocaleString(getDateLocale())}
                  </time>
                </span>
                <span className="mt-1 block break-words font-medium">
                  {message.call
                    ? callHeadline(message.call, t)
                    : message.channel === 'call' && message.direction === 'outbound'
                      ? unplacedCallLabel(message, t)
                      : message.subject || t('customers.communication.readMessage')}
                </span>
                <span className="mt-1 line-clamp-2 break-words text-[color:var(--color-muted)]">
                  {messagePreview(message)}
                </span>
                <span className="mt-1 flex flex-wrap gap-2 text-xs text-[color:var(--color-muted)]">
                  {message.channel !== 'internal' && message.channel !== 'call' ? (
                    <span>{t(`customers.communication.status.${deliveryStatus(message.status)}`)}</span>
                  ) : null}
                  {message.attachments.length ? (
                    <span>{t('customers.communication.attachments', { count: message.attachments.length })}</span>
                  ) : null}
                  <span className="underline">{t('customers.communication.readMessage')}</span>
                </span>
              </summary>
              <ol className="m-3 mt-0">
                <MessageRow
                  message={message}
                  customerId={customerId}
                  onReply={
                    canWrite && isAnswerableInbound(message)
                      ? () => {
                          setComposing(true);
                          setReply({
                            channel: replyChannelFor(message),
                            subject: message.subject,
                            replyToAddress: message.replyToAddress,
                            nonce: ++replyNonce.current,
                          });
                        }
                      : undefined
                  }
                />
              </ol>
            </details>
          </li>
        ))}
      </ol>
      {history ? (
        <nav
          className="mt-4 flex flex-wrap items-center justify-between gap-3"
          aria-label={t('customers.communication.pagination')}
        >
          <Button disabled={page <= 1 || thread.isFetching} onClick={() => changePage(page - 1)}>
            {t('customers.communication.newer')}
          </Button>
          <span className="text-sm" aria-live="polite">
            {t('customers.communication.page', { page })}
          </span>
          <Button
            disabled={loading || failed || thread.isFetching || ordered.length < PAGE_SIZE}
            onClick={() => changePage(page + 1)}
          >
            {t('customers.communication.older')}
          </Button>
        </nav>
      ) : null}
    </section>
  );
}

/** "Voicemail · 0:42" or "Missed call", for a call's row in the history. */
function callHeadline(call: NonNullable<ConversationMessage['call']>, t: TFunction) {
  const what = callOutcomeLabel(call, t);
  const seconds = callDurationSeconds(call);
  return seconds !== null ? `${what} · ${formatCallDuration(seconds)}` : what;
}

function messagePreview(message: ConversationMessage) {
  const text = (message.body ?? message.call?.from ?? '').replace(/\s+/g, ' ').trim();
  return text.length > 160 ? `${text.slice(0, 160)}…` : text;
}

function deliveryStatus(status: string) {
  return ['sent', 'delivered', 'failed', 'undelivered', 'unknown', 'suppressed', 'received'].includes(status)
    ? status
    : 'queued';
}
